import { Injectable, signal, computed } from '@angular/core';
import {
  AuthenticateSuccessData,
  ContractSummary,
  CustomerContactRequest,
  SessionClaims,
  VinDecoded,
  VinEligibilityData,
} from '@core/models';

const SESSION_STORAGE_KEY = 'vin_portal_session';
const REDIRECT_URL_KEY = 'vin_portal_redirect_url';
const SESSION_SOURCE_KEY = 'vin_portal_session_source';

export type SessionSource = 'CONSUMER' | 'DMS';

export interface AuthFormValues {
  vin: string;
  lastName: string;
  zip: string;
}

interface StoredSession {
  token: string;
  expiresAt: string;
  contractContextId: string;
  contractSummary?: ContractSummary;
  authForm?: AuthFormValues;
  /** Last successfully looked-up VIN on VIN entry page; restored on refresh to re-run lookup */
  vinEntryVin?: string;
  /** Decoded vehicle info for review page restore on refresh */
  vinEntryDecoded?: VinDecoded;
  /** Eligibility result for review page and guard on refresh */
  vinEntryEligibility?: VinEligibilityData;
  /** Customer contact captured on VIN entry before review or exception submit */
  vinEntryCustomerContact?: CustomerContactRequest;
  /** True after a secondary-VIN exception was submitted in this session (LSVP-96) */
  exceptionWorkflowComplete?: boolean;
  /** Case reference from the completed exception submit (optional) */
  exceptionCaseRef?: string;
}

/**
 * Service for managing user session state
 * Stores JWT tokens in sessionStorage for security
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  // Private writable signals
  private readonly _token = signal<string | null>(null);
  private readonly _contractContextId = signal<string | null>(null);
  private readonly _expiresAt = signal<Date | null>(null);
  private readonly _contractSummary = signal<ContractSummary | null>(null);
  private readonly _authForm = signal<AuthFormValues | null>(null);
  private readonly _vinEntryVin = signal<string | null>(null);
  private readonly _vinEntryDecoded = signal<VinDecoded | null>(null);
  private readonly _vinEntryEligibility = signal<VinEligibilityData | null>(null);
  private readonly _vinEntryCustomerContact = signal<CustomerContactRequest | null>(null);
  private readonly _exceptionWorkflowComplete = signal(false);
  private readonly _exceptionCaseRef = signal<string | null>(null);

  // Public readonly signals
  readonly token = this._token.asReadonly();
  readonly contractContextId = this._contractContextId.asReadonly();
  readonly expiresAt = this._expiresAt.asReadonly();
  readonly contractSummary = this._contractSummary.asReadonly();

  // Computed signals
  readonly isAuthenticated = computed(() => {
    const token = this._token();
    return !!token && !this.isExpired();
  });

  readonly timeUntilExpiry = computed(() => {
    const expiresAt = this._expiresAt();
    if (!expiresAt) return 0;
    return Math.max(0, expiresAt.getTime() - Date.now());
  });

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Set session data after successful authentication.
   * Optionally store auth form values (VIN, lastName, zip) so they can be restored on refresh.
   * If the user changed personal info (vin, lastName, zip) vs previously stored values, the
   * VIN entry page state is cleared so they must re-enter the vehicle-to-add VIN.
   */
  setSession(data: AuthenticateSuccessData, authForm?: AuthFormValues): void {
    this._token.set(data.sessionToken);
    this._contractContextId.set(data.contractContextId);
    this._expiresAt.set(new Date(data.sessionExpiresAt));
    this._contractSummary.set(data.contractSummary ?? null);
    this._authForm.set(authForm ?? null);

    const oldStored = this.getStoredSession();
    const authUnchanged =
      oldStored?.authForm && authForm && this.authFormValuesEqual(oldStored.authForm, authForm);

    const sessionToSave: StoredSession = {
      token: data.sessionToken,
      expiresAt: data.sessionExpiresAt,
      contractContextId: data.contractContextId,
      contractSummary: data.contractSummary,
      authForm,
    };

    if (authUnchanged && oldStored) {
      sessionToSave.vinEntryVin = oldStored.vinEntryVin;
      sessionToSave.vinEntryDecoded = oldStored.vinEntryDecoded;
      sessionToSave.vinEntryEligibility = oldStored.vinEntryEligibility;
      this._vinEntryVin.set(oldStored.vinEntryVin ?? null);
      this._vinEntryDecoded.set(oldStored.vinEntryDecoded ?? null);
      this._vinEntryEligibility.set(oldStored.vinEntryEligibility ?? null);
    } else {
      this._vinEntryVin.set(null);
      this._vinEntryDecoded.set(null);
      this._vinEntryEligibility.set(null);
    }

    this.saveToStorage(sessionToSave);
  }

  /** Compare auth form values (normalized) to detect if user changed personal info. */
  private authFormValuesEqual(a: AuthFormValues, b: AuthFormValues): boolean {
    const n = (v: string) => (v ?? '').trim().toUpperCase().replace(/\s/g, '');
    return n(a.vin) === n(b.vin) && n(a.lastName) === n(b.lastName) && n(a.zip) === n(b.zip);
  }

  /**
   * Get stored auth form values for repopulating the authenticate form after refresh.
   */
  getAuthFormValues(): AuthFormValues | null {
    return this._authForm();
  }

  /**
   * Set or clear the VIN from the VIN entry page (persisted so we can repopulate and re-run lookup on refresh).
   * Clearing also clears decoded and eligibility.
   */
  setVinEntryVin(vin: string | null): void {
    this._vinEntryVin.set(vin);
    if (vin === null) {
      this._vinEntryDecoded.set(null);
      this._vinEntryEligibility.set(null);
      this._vinEntryCustomerContact.set(null);
    }
    this.persistVinEntryToStorage();
  }

  /** Persist VIN entry fields (vin, decoded, eligibility) into stored session. */
  private persistVinEntryToStorage(): void {
    const stored = this.getStoredSession();
    if (stored) {
      stored.vinEntryVin = this._vinEntryVin() ?? undefined;
      stored.vinEntryDecoded = this._vinEntryDecoded() ?? undefined;
      stored.vinEntryEligibility = this._vinEntryEligibility() ?? undefined;
      stored.vinEntryCustomerContact = this._vinEntryCustomerContact() ?? undefined;
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
    }
  }

  /**
   * Get stored VIN entry for repopulating and re-running lookup on refresh.
   */
  getVinEntryVin(): string | null {
    return this._vinEntryVin();
  }

  /**
   * Set decoded vehicle info for review page restore on refresh.
   */
  setVinEntryDecoded(decoded: VinDecoded | null): void {
    this._vinEntryDecoded.set(decoded);
    this.persistVinEntryToStorage();
  }

  getVinEntryDecoded(): VinDecoded | null {
    return this._vinEntryDecoded();
  }

  /**
   * Set eligibility result for review page and guard on refresh.
   */
  setVinEntryEligibility(result: VinEligibilityData | null): void {
    this._vinEntryEligibility.set(result);
    this.persistVinEntryToStorage();
  }

  getVinEntryEligibility(): VinEligibilityData | null {
    return this._vinEntryEligibility();
  }

  setVinEntryCustomerContact(contact: CustomerContactRequest | null): void {
    this._vinEntryCustomerContact.set(contact);
    this.persistVinEntryToStorage();
  }

  getVinEntryCustomerContact(): CustomerContactRequest | null {
    return this._vinEntryCustomerContact();
  }

  /**
   * Mark the secondary-VIN exception workflow as complete (persisted for refresh).
   * Blocks VIN Entry / review / exception form until the session is cleared (Home).
   */
  markExceptionWorkflowComplete(caseRef?: string | null): void {
    this._exceptionWorkflowComplete.set(true);
    this._exceptionCaseRef.set(caseRef?.trim() ? caseRef.trim() : null);
    this.persistExceptionWorkflowToStorage();
  }

  isExceptionWorkflowComplete(): boolean {
    return this._exceptionWorkflowComplete();
  }

  getExceptionCaseRef(): string | null {
    return this._exceptionCaseRef();
  }

  private persistExceptionWorkflowToStorage(): void {
    const stored = this.getStoredSession();
    if (!stored) return;
    stored.exceptionWorkflowComplete = this._exceptionWorkflowComplete() || undefined;
    stored.exceptionCaseRef = this._exceptionCaseRef() ?? undefined;
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
  }

  /**
   * Clear session data (logout)
   */
  clearSession(): void {
    this._token.set(null);
    this._contractContextId.set(null);
    this._expiresAt.set(null);
    this._contractSummary.set(null);
    this._authForm.set(null);
    this._vinEntryVin.set(null);
    this._vinEntryDecoded.set(null);
    this._vinEntryEligibility.set(null);
    this._vinEntryCustomerContact.set(null);
    this._exceptionWorkflowComplete.set(false);
    this._exceptionCaseRef.set(null);

    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(REDIRECT_URL_KEY);
    sessionStorage.removeItem(SESSION_SOURCE_KEY);
  }

  /**
   * Mark the active session as originating from a DMS staff member (on-behalf
   * submission) or a direct consumer. Persisted in sessionStorage so the flag
   * survives navigation within the SPA. Cleared on logout.
   */
  setSessionSource(source: SessionSource): void {
    sessionStorage.setItem(SESSION_SOURCE_KEY, source);
  }

  /**
   * Return the active session source, defaulting to CONSUMER when nothing was
   * explicitly set (typical direct-customer entry).
   */
  getSessionSource(): SessionSource {
    return sessionStorage.getItem(SESSION_SOURCE_KEY) === 'DMS' ? 'DMS' : 'CONSUMER';
  }

  /** Convenience: true when the active session is a DMS staff session. */
  isStaffSession(): boolean {
    return this.getSessionSource() === 'DMS';
  }

  /**
   * Get the current token (for HTTP interceptor)
   */
  getToken(): string | null {
    if (this.isExpired()) {
      this.clearSession();
      return null;
    }
    return this._token();
  }

  /**
   * Check if the session is expired
   */
  isExpired(): boolean {
    const expiresAt = this._expiresAt();
    if (!expiresAt) return true;
    return Date.now() >= expiresAt.getTime();
  }

  /**
   * Decode JWT claims (without verification)
   */
  getClaims(): SessionClaims | null {
    const token = this._token();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      return payload as SessionClaims;
    } catch {
      return null;
    }
  }

  /**
   * Set URL to redirect to after authentication
   */
  setRedirectUrl(url: string): void {
    if (this.isRelativePath(url)) {
      sessionStorage.setItem(REDIRECT_URL_KEY, url);
    }
  }

  /**
   * Get and clear the redirect URL
   */
  consumeRedirectUrl(): string | null {
    const url = sessionStorage.getItem(REDIRECT_URL_KEY);
    sessionStorage.removeItem(REDIRECT_URL_KEY);
    if (url && !this.isRelativePath(url)) {
      return null;
    }
    return url;
  }

  private isRelativePath(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }

  /**
   * Update the contract summary (e.g., after VIN commit)
   */
  updateContractSummary(summary: ContractSummary): void {
    this._contractSummary.set(summary);

    const stored = this.getStoredSession();
    if (stored) {
      stored.contractSummary = summary;
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
    }
  }

  /**
   * Load session from sessionStorage on service init
   */
  private loadFromStorage(): void {
    const stored = this.getStoredSession();
    if (!stored) return;

    const expiresAt = new Date(stored.expiresAt);
    if (Date.now() >= expiresAt.getTime()) {
      // Session expired, clear it
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }

    this._token.set(stored.token);
    this._contractContextId.set(stored.contractContextId);
    this._expiresAt.set(expiresAt);
    this._contractSummary.set(stored.contractSummary ?? null);
    this._authForm.set(stored.authForm ?? null);
    this._vinEntryVin.set(stored.vinEntryVin ?? null);
    this._vinEntryDecoded.set(stored.vinEntryDecoded ?? null);
    this._vinEntryEligibility.set(stored.vinEntryEligibility ?? null);
    this._vinEntryCustomerContact.set(stored.vinEntryCustomerContact ?? null);
    this._exceptionWorkflowComplete.set(!!stored.exceptionWorkflowComplete);
    this._exceptionCaseRef.set(stored.exceptionCaseRef ?? null);
  }

  /**
   * Save session to sessionStorage
   */
  private saveToStorage(session: StoredSession): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  }

  /**
   * Get stored session from sessionStorage
   */
  private getStoredSession(): StoredSession | null {
    try {
      const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }
}
